import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import { defaultPlacement, placementLabel, placementsFor } from '../../utils/placements';
import {
  DropdownPosition,
  dropdownWrapperStyle,
  positionDropdown,
} from '../../utils/dropdownPosition';
import { useFollowAnchor } from '../../hooks/useFollowAnchor';

const MENU_WIDTH = 168;
/** Estimated per-row height — side-of-anchor decision only, never a cap. */
const ROW_ESTIMATE = 38;

/**
 * Placement picker for the tended-card row: a frost trigger showing the
 * effective surface (a null value reads as the channel default) and a
 * .tend-menu portal popover. Channel-generic — renders nothing for
 * single-surface channels, so instagram/facebook inherit it the moment
 * they join PLACEMENTS_BY_CHANNEL.
 */
const PlacementControl: React.FC<{
  channel: string;
  value: string | null;
  onChange: (placement: string) => void;
  /** The record of a published post: the surface shows as a label, no menu. */
  disabled?: boolean;
}> = ({ channel, value, onChange, disabled = false }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<DropdownPosition | null>(null);

  const options = placementsFor(channel);
  const rowCount = options?.length ?? 0;

  const open = useCallback(() => {
    const anchor = triggerRef.current?.getBoundingClientRect();
    if (!anchor) return;
    setPos(positionDropdown(anchor, MENU_WIDTH, rowCount * ROW_ESTIMATE + 16));
  }, [rowCount]);
  const close = useCallback(() => setPos(null), []);
  // Follow the trigger on scroll; close when it's covered or off-screen.
  useFollowAnchor(pos !== null, triggerRef, open, close, portalRef);

  if (!options) return null;
  const current = value ?? defaultPlacement(channel)!;

  if (disabled) {
    return (
      <span
        className="flex items-center rounded-md border border-white/60 bg-white/45 px-2 py-1 text-xs font-medium text-gray-700"
        title="Placement"
        data-testid="placement-locked"
      >
        {placementLabel(current, channel)}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (pos ? setPos(null) : open())}
        className={`flex items-center gap-1 rounded-md border border-white/60 px-2 py-1 text-xs font-medium text-gray-700 transition-colors ${
          pos ? 'bg-white/70' : 'bg-white/45 hover:bg-white/65'
        }`}
        title="Placement"
      >
        {placementLabel(current, channel)}
        <svg className="h-3 w-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {pos &&
        createPortal(
          <div ref={portalRef}>
            {/* Above the TENDED modal (999999) like the aspect popover. */}
            <div style={{ position: 'fixed', inset: 0, zIndex: Z.ABOVE_ITEM }} onClick={() => setPos(null)} />
            <div style={dropdownWrapperStyle(pos, 1000001)}>
              <div className="tend-menu tend-surface" role="menu" style={{ width: MENU_WIDTH, fontSize: 13 }}>
                {options.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`tend-menu-item${p === current ? ' is-active' : ''}`}
                    onClick={() => {
                      setPos(null);
                      if (p !== current) onChange(p);
                    }}
                  >
                    {placementLabel(p, channel)}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PlacementControl;
